/**
 * FRONT-2220: Configuration → "Your MCP servers" — which of the user's
 * Connections servers (personal and org-shared) the builder may use in this
 * session. A change applies from the builder's next turn; a server that is
 * broken at that point is skipped with a note in the transcript, never fails
 * the turn.
 */
import { useQuery } from '@tanstack/react-query';
import { PlugIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { codingKeys } from '~/lib/query-keys';
import { CapabilityCard } from '~/features/apps/capabilities-section';
import { useSetCodingMcpServers } from '~/hooks/mutations/use-coding-mutations';
import type { LocalMcpStatus } from '~/hooks/queries/use-local-mcps';

function stateLabel(server: LocalMcpStatus): string {
  if (server.state === 'connected') return `${server.toolCount} tool${server.toolCount === 1 ? '' : 's'}`;
  if (server.state === 'error') return server.needsConnect ? 'Sign in on Connections' : 'Not reachable';
  return server.state === 'pending' ? 'Connecting…' : 'Switched off on Connections';
}

export function BuilderMcpServers({ sessionId }: { sessionId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: codingKeys.mcpServers(sessionId),
    queryFn: () =>
      api.get<{ servers: Array<LocalMcpStatus & { attached: boolean }> }>(
        `/api/coding/sessions/${sessionId}/mcp-servers`,
      ),
  });
  const save = useSetCodingMcpServers();

  if (isLoading) return <p className="text-xs text-muted-foreground">Loading your servers…</p>;
  const servers = data?.servers ?? [];
  if (servers.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        You have no MCP servers yet. Add one on Connections, then pick it here.
      </p>
    );
  }
  const toggle = (name: string, on: boolean) => {
    const next = servers.filter((s) => (s.name === name ? on : s.attached)).map((s) => s.name);
    save.mutate({ sessionId, servers: next }, { onError: (e) => toast.error(`Couldn't save: ${(e as Error).message}`) });
  };

  return (
    <div className="flex flex-col gap-2">
      {servers.map((server) => {
        const on = server.attached;
        return (
          <CapabilityCard
            key={server.name}
            icon={<PlugIcon size={18} />}
            name={server.name}
            description={server.error ?? (server.org ? 'Shared by your workspace' : 'Your own server')}
            enabled={on}
            statusLabel={on ? stateLabel(server) : 'Off'}
            pending={save.isPending}
            // A broken server can still be switched off, never on.
            locked={!on && server.state !== 'connected'}
            onToggle={(next) => toggle(server.name, next)}
          />
        );
      })}
    </div>
  );
}
